export interface InterviewGuideRoleGuide {
  title: string;
  focus?: string;
  questions: string[];
}

export interface InterviewGuideProbe {
  response: string;
  probe: string;
}

export interface InterviewGuideRedFlag {
  label: string;
  detail: string;
}

export interface InterviewGuideUnparsedSection {
  title: string;
  markdown: string;
}

export interface InterviewGuideReviewModel {
  filename: string;
  contextMarkdown?: string;
  assumptionsMarkdown?: string;
  roleGuides: InterviewGuideRoleGuide[];
  probeBank: InterviewGuideProbe[];
  redFlags: InterviewGuideRedFlag[];
  noteCaptureTemplate?: string;
  nextStep?: string;
  unparsedSections: InterviewGuideUnparsedSection[];
  isStructured: boolean;
  fallbackMarkdown?: string;
}

interface Section {
  title: string;
  level: number;
  lines: string[];
}

const DEFAULT_FILENAME = '01-interview-guide.md';
const DIVIDER_PATTERN = /^\s*-{7,}\s*$/;
const ROLE_GUIDES_HEADING = 'role-specific question guides';
const NOTE_CAPTURE_HEADING = 'note-capture template';
const RED_FLAGS_HEADING = 'red flags to watch for';

export function parseInterviewGuideMarkdown(
  markdown: string | undefined | null,
  filename = DEFAULT_FILENAME,
): InterviewGuideReviewModel {
  const source = normalizeMarkdown(markdown);
  const emptyModel = createEmptyModel(filename, source);

  if (!source.trim()) {
    return emptyModel;
  }

  const { beforeDivider, afterDivider } = splitOnReviewDivider(source);
  const sections = parseSections(afterDivider);
  const roleStartIndex = findSectionIndex(sections, ROLE_GUIDES_HEADING);

  if (roleStartIndex < 0) {
    return {
      ...emptyModel,
      fallbackMarkdown: source,
    };
  }

  const context = parseContextMarkdown(beforeDivider);
  const probeIndex = findSectionIndex(sections, 'probe bank');
  const redFlagsIndex = findSectionIndex(sections, RED_FLAGS_HEADING);
  const noteIndex = findSectionIndex(sections, NOTE_CAPTURE_HEADING);
  const nextStep = extractNextStep(sections);
  const roleEndIndex = lowestPositiveIndex([probeIndex, noteIndex, sections.length]);
  const roleGuides = parseRoleGuides(sections.slice(roleStartIndex + 1, roleEndIndex));
  const probeBank = probeIndex >= 0 ? parseProbeBank(sections[probeIndex]) : [];
  const redFlags = redFlagsIndex >= 0 ? parseRedFlags(sections[redFlagsIndex]) : [];
  const noteCaptureTemplate =
    noteIndex >= 0 ? parseNoteCaptureTemplate(sections[noteIndex]) : undefined;
  const unparsedSections = parseUnparsedSections(sections, {
    roleStartIndex,
    roleEndIndex,
    probeIndex,
    redFlagsIndex,
    noteIndex,
  });

  return {
    filename,
    ...context,
    roleGuides,
    probeBank,
    redFlags,
    noteCaptureTemplate,
    nextStep,
    unparsedSections,
    isStructured: roleGuides.length > 0,
    fallbackMarkdown: roleGuides.length > 0 ? undefined : source,
  };
}

function createEmptyModel(filename: string, fallbackMarkdown?: string): InterviewGuideReviewModel {
  return {
    filename,
    roleGuides: [],
    probeBank: [],
    redFlags: [],
    unparsedSections: [],
    isStructured: false,
    fallbackMarkdown,
  };
}

function normalizeMarkdown(markdown: string | undefined | null): string {
  return (markdown ?? '').replaceAll(/\r\n?/g, '\n').trim();
}

function splitOnReviewDivider(markdown: string): { beforeDivider: string; afterDivider: string } {
  const lines = markdown.split('\n');
  const dividerIndex = lines.findIndex((line) => DIVIDER_PATTERN.test(line));

  if (dividerIndex < 0) {
    return { beforeDivider: '', afterDivider: markdown };
  }

  return {
    beforeDivider: lines.slice(0, dividerIndex).join('\n').trim(),
    afterDivider: lines
      .slice(dividerIndex + 1)
      .join('\n')
      .trim(),
  };
}

function parseSections(markdown: string): Section[] {
  const sections: Section[] = [];
  let current: Section | undefined;

  for (const line of markdown.split('\n')) {
    const heading = /^(#{1,6})\s+(.+?)\s*$/.exec(line);

    if (heading) {
      current = {
        title: heading[2].trim(),
        level: heading[1].length,
        lines: [],
      };
      sections.push(current);
      continue;
    }

    if (current) {
      current.lines.push(line);
    }
  }

  return sections;
}

function normalizeHeading(heading: string): string {
  return heading.trim().toLowerCase().replaceAll(/[—–]/g, '-').replaceAll(/\s+/g, ' ');
}

function findSectionIndex(sections: Section[], headingNeedle: string): number {
  const normalizedNeedle = normalizeHeading(headingNeedle);
  return sections.findIndex((section) =>
    normalizeHeading(section.title).includes(normalizedNeedle),
  );
}

function lowestPositiveIndex(indices: number[]): number {
  return Math.min(...indices.filter((index) => index >= 0));
}

function parseContextMarkdown(markdown: string): {
  contextMarkdown?: string;
  assumptionsMarkdown?: string;
} {
  if (!markdown.trim()) {
    return {};
  }

  const sections = parseSections(markdown);
  const assumptionsIndex = sections.findIndex((section) =>
    normalizeHeading(section.title).includes('assumptions'),
  );

  if (assumptionsIndex < 0) {
    return { contextMarkdown: markdown };
  }

  const assumptionsSection = sections[assumptionsIndex];
  const assumptionsMarkdown = sectionToMarkdown(assumptionsSection);
  const assumptionsBlockPattern = new RegExp(escapeRegExp(assumptionsMarkdown), 'm');
  const contextMarkdown = markdown.replace(assumptionsBlockPattern, '').trim();

  return {
    contextMarkdown: contextMarkdown || undefined,
    assumptionsMarkdown,
  };
}

function parseRoleGuides(sections: Section[]): InterviewGuideRoleGuide[] {
  return sections
    .filter(
      (section) => section.level === 2 && normalizeHeading(section.title) !== RED_FLAGS_HEADING,
    )
    .map((section) => {
      const lines = section.lines.map((line) => line.trim()).filter(Boolean);
      const focus = lines.find((line) => !isNumberedListLine(line));
      const questions = lines
        .filter(isNumberedListLine)
        .map((line) => line.replace(/^\d+[.)]\s+/, '').trim())
        .filter(Boolean);

      return {
        title: section.title,
        focus: focus ? normalizeFocusLine(focus) : undefined,
        questions,
      };
    })
    .filter((guide) => guide.questions.length > 0 || Boolean(guide.focus));
}

function normalizeFocusLine(focus: string): string {
  return /^focus\s*:/i.test(focus) ? focus.replace(/^focus\s*:\s*/i, '').trim() : focus;
}

function isNumberedListLine(line: string): boolean {
  return /^\d+[.)]\s+\S/.test(line.trim());
}

function parseProbeBank(section: Section): InterviewGuideProbe[] {
  return section.lines
    .map((line) => line.trim())
    .filter((line) => line.startsWith('- '))
    .map((line) => {
      const match = /^-\s+\*\*(.+?)\*\*\s*(.*)$/.exec(line);
      if (!match) {
        return undefined;
      }

      return {
        response: match[1].trim(),
        probe: match[2].trim(),
      };
    })
    .filter((probe): probe is InterviewGuideProbe => Boolean(probe));
}

function parseRedFlags(section: Section): InterviewGuideRedFlag[] {
  return section.lines
    .map((line) => line.trim())
    .filter((line) => line.startsWith('- '))
    .map((line) => {
      const cleanLine = line.replace(/^-\s+/, '');
      const [label, ...detailParts] = cleanLine.split(/\s[-—–]\s/);

      if (!label || detailParts.length === 0) {
        return undefined;
      }

      return {
        label: label.trim(),
        detail: detailParts.join(' - ').trim(),
      };
    })
    .filter((redFlag): redFlag is InterviewGuideRedFlag => Boolean(redFlag));
}

function parseNoteCaptureTemplate(section: Section): string | undefined {
  const lines = removeTrailingNextStep(section.lines).join('\n').trim();
  return lines || undefined;
}

function extractNextStep(sections: Section[]): string | undefined {
  for (const section of sections) {
    const line = section.lines.find((candidate) => /^\s*\*\*Next step:\*\*/i.test(candidate));
    if (line) {
      return line.trim();
    }
  }

  return undefined;
}

function removeTrailingNextStep(lines: string[]): string[] {
  const nextStepIndex = lines.findIndex((line) => /^\s*\*\*Next step:\*\*/i.test(line));
  return nextStepIndex >= 0 ? lines.slice(0, nextStepIndex) : lines;
}

function parseUnparsedSections(
  sections: Section[],
  known: {
    roleStartIndex: number;
    roleEndIndex: number;
    probeIndex: number;
    redFlagsIndex: number;
    noteIndex: number;
  },
): InterviewGuideUnparsedSection[] {
  return sections
    .map((section, index) => ({ section, index }))
    .filter(({ section, index }) => {
      if (
        index === known.roleStartIndex ||
        index === known.probeIndex ||
        index === known.noteIndex
      ) {
        return false;
      }

      if (index > known.roleStartIndex && index < known.roleEndIndex) {
        return false;
      }

      if (index === known.redFlagsIndex) {
        return false;
      }

      return normalizeHeading(section.title) !== RED_FLAGS_HEADING;
    })
    .map(({ section }) => ({
      title: section.title,
      markdown: sectionToMarkdown(section),
    }))
    .filter((section) => section.markdown.trim());
}

function sectionToMarkdown(section: Section): string {
  const heading = `${'#'.repeat(section.level)} ${section.title}`;
  return [heading, ...section.lines].join('\n').trim();
}

function escapeRegExp(value: string): string {
  return value.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}
